import { useCallback, useEffect, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import {
  Box,
  Button,
  Container,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Invitation, ValidationError } from "baptize-america-client";
import { useAlert } from "../../components/AlertProvider";
import { LoginWindow } from "../../components/LoginWindow";
import TopNavBar from "../../components/TopNavBar";
import {
  createChurchInvitation,
  deleteInvitation,
  listInvitation,
} from "../../utils/client";
import { handleSort, isAdmin, stringFromDateTz } from "../../utils/helper";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useUserData } from "../../components/DataProvider";

const InvitationsManager = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { setAlert } = useAlert();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [toBeDeleted, setToBeDeleted] = useState<Invitation | undefined>(
    undefined,
  );
  const [orderBy, setOrderBy] = useState<keyof Invitation>("name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const { user, authStatus } = useAuthenticator((context) => [context.user]);
  const { attributes } = useUserData();

  const styles = {
    navBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexDirection: "column",
      mb: 5,
    },
  };

  const loadInvitations = useCallback(async () => {
    if (!isAdmin(attributes)) {
      setAlert({
        showAlert: true,
        alertMsg: "Only Admins can view this page",
        alertType: "error",
      });
      return;
    }

    try {
      const result = await listInvitation();
      setInvitations(result.invitations ?? []);
    } catch (err) {
      setInvitations([]);
      console.log("Error loading invitations", err);
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error Loading Invitations",
        alertType: "error",
      });
    }
  }, [attributes, setAlert]);

  const resendInvite = async (email: string, name: string) => {
    if (!isAdmin(attributes)) {
      console.log("Only Admins can send invitations!");
      setAlert({
        showAlert: true,
        alertMsg: "Only Admins can send invitations.",
        alertType: "error",
      });
      return;
    }

    try {
      await createChurchInvitation({ name, email });
      setAlert({
        showAlert: true,
        alertMsg: `An invitation has been sent to: ${email}`,
        alertType: "success",
      });
      await loadInvitations();
    } catch (err) {
      console.log("Error creating church invitation", err);
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error Creating Church Invitation",
        alertType: "error",
      });
    }
  };

  const deleteInvite = async (invitationId: string, email: string) => {
    if (!isAdmin(attributes)) {
      console.log("Only Admins can delete invitations");
      setAlert({
        showAlert: true,
        alertMsg: "Only Admins can delete invitations.",
        alertType: "error",
      });
      return;
    }

    try {
      await deleteInvitation({ invitationId });
      setAlert({
        showAlert: true,
        alertMsg: `An invitation for ${email} has been deleted`,
        alertType: "warning",
      });
      await loadInvitations();
    } catch (err) {
      console.log("Error deleting church invitation", err);
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error Deleting Church Invitation",
        alertType: "error",
      });
    }
  };

  useEffect(() => {
    if (user === undefined) return;
    loadInvitations();
  }, [user, loadInvitations]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy!", err);
    }
  };

  const InvitationRow = ({ invite }: { invite: Invitation }) => {
    const inviteUrl = `https://${window.location.hostname}/churches/create?id=${invite.invitationId}`;
    return (
      <TableRow>
        <TableCell>{invite.name}</TableCell>
        <TableCell>{invite.email}</TableCell>
        <TableCell>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Link
              href={inviteUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {inviteUrl}
            </Link>
            <Tooltip title="Copy to clipboard">
              <IconButton size="small" onClick={() => handleCopy(inviteUrl)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </TableCell>
        <TableCell>
          {invite.ttl === undefined
            ? "unknown"
            : stringFromDateTz(invite.ttl, "UTC")}
        </TableCell>
        <TableCell sx={{ minWidth: "13rem" }}>
          <Button
            sx={{ backgroundColor: "#d32f2f", color: "#FFF", mr: 1 }}
            onClick={() => setToBeDeleted(invite)}
          >
            Delete
          </Button>
          <Button
            variant="outlined"
            onClick={() => resendInvite(invite.email ?? "", invite.name ?? "")}
          >
            Resend
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  return authStatus === "authenticated" && isAdmin(attributes) ? (
    <div>
      <Container maxWidth={isLargeScreen ? "md" : false}>
        <Box
          sx={[
            {
              "& .MuiTextField-root": {
                m: 1,
                width: theme.breakpoints.values.sm,
              },
            },
            styles.navBar,
          ]}
        >
          <Box sx={{ mb: 4 }}>
            <TopNavBar showBackArrow={true} />
          </Box>
          <TableContainer sx={{ width: "95vw" }} component={Paper}>
            <Table size="small" stickyHeader={true}>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={orderBy === "name" ? order : false}>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={() =>
                        handleSort<Invitation>(
                          "name",
                          orderBy,
                          order,
                          setOrder,
                          setOrderBy,
                          setInvitations,
                        )
                      }
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={orderBy === "email" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "email"}
                      direction={orderBy === "email" ? order : "asc"}
                      onClick={() =>
                        handleSort<Invitation>(
                          "email",
                          orderBy,
                          order,
                          setOrder,
                          setOrderBy,
                          setInvitations,
                        )
                      }
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Invitation URL</TableCell>
                  <TableCell sortDirection={orderBy === "ttl" ? order : false}>
                    <TableSortLabel
                      active={orderBy === "ttl"}
                      direction={orderBy === "ttl" ? order : "asc"}
                      onClick={() =>
                        handleSort<Invitation>(
                          "ttl",
                          orderBy,
                          order,
                          setOrder,
                          setOrderBy,
                          setInvitations,
                        )
                      }
                    >
                      Expiration
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(invitations ?? []).map((invitation) => {
                  return (
                    <InvitationRow
                      key={invitation.invitationId}
                      invite={invitation}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Container>
      <ConfirmDialog
        open={!!toBeDeleted}
        onClose={() => setToBeDeleted(undefined)}
        onConfirm={() =>
          deleteInvite(toBeDeleted?.invitationId!!, toBeDeleted?.email ?? "")
        }
        title={`Delete invitation for ${toBeDeleted?.name}?`}
        content={`Delete invitation for ${toBeDeleted?.name}: ${toBeDeleted?.email}? Emails with links have **already** been sent! These **links will stop working!!** Continue?`}
      />
    </div>
  ) : (
    <LoginWindow />
  );
};

export default InvitationsManager;
